<template>
  <div class="margin-property-details-card" :class="className">
    <v-card class="property-details elevation-0 pa-4">
      <v-list dense disabled>
        <h3 class="font-weight-bold mb-sm-4 mb-md-0 ml-md-5">
          Your property details
        </h3>
        <v-list-item-group class="mt-sm-3 ml-md-7">
          <v-list-item class="px-sm-0">
            <v-list-item-icon class="mr-sm-1 mr-md-4">
              <v-icon color="#272727">icon-property-type</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="d-flex align-center">
                <span class="mr-sm-2 font-14 font-md-18">Property type:</span>
                <span class="font-14 font-md-18">
                  <strong>{{ propertyTypeText }}</strong></span
                >
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item class="px-sm-0">
            <v-list-item-icon class="mr-sm-1 mr-md-4">
              <v-icon color="#272727">icon-build-year</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="d-flex align-center">
                <span class="mr-sm-2 font-14 font-md-18">Build year:</span>
                <span class="font-14 font-md-18">
                  <strong>{{ propertyDetails.BuildYear }}</strong></span
                >
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item class="px-sm-0">
            <v-list-item-icon class="mr-sm-1 mr-md-4">
              <v-icon color="#272727">icon-bedrooms</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="d-flex align-center">
                <span class="mr-sm-2 font-14 font-md-18"
                  >Number of bedrooms:</span
                >
                <span class="font-14 font-md-18">
                  <strong>{{ numberOfBedroomsText }}&nbsp;</strong>
                </span>
                <v-icon
                  v-if="
                    propertyDetails.NumberOfBedrooms === illegalNumberOfRooms
                  "
                  color="var(--red-500)"
                  >mdi-alert-circle</v-icon
                >
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item class="px-sm-0">
            <v-list-item-icon class="mr-sm-1 mr-md-4">
              <v-icon color="#272727">icon-bathrooms</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="d-flex align-center">
                <span class="mr-sm-2 font-14 font-md-18"
                  >Number of bathrooms:</span
                >
                <span class="font-14 font-md-18">
                  <strong>{{ numberOfBathroomsText }}&nbsp;</strong>
                </span>
                <v-icon
                  v-if="
                    propertyDetails.NumberOfBathrooms === illegalNumberOfRooms
                  "
                  color="var(--red-500)"
                  >mdi-alert-circle</v-icon
                >
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item class="px-sm-0">
            <v-list-item-icon class="mr-sm-1 mr-md-4">
              <v-icon color="#272727">icon-wall-type</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="d-flex align-center">
                <span class="mr-sm-2 font-14 font-md-18">Wall type:</span>
                <span class="font-14 font-md-18"
                  ><strong>{{ wallTypeText }}</strong></span
                >
                <v-icon v-if="isWallTypeOther" color="var(--red-500)"
                  >mdi-alert-circle</v-icon
                >
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item class="px-sm-0">
            <v-list-item-icon class="mr-sm-1 mr-md-4">
              <v-icon color="#272727">icon-roof-type</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="d-flex align-center">
                <span class="mr-sm-2 font-14 font-md-18">Roof type:</span>
                <span class="font-14 font-md-18"
                  ><strong>{{ roofTypeText }}</strong></span
                >
                <v-icon v-if="isRoofTypeOther" color="var(--red-500)"
                  >mdi-alert-circle</v-icon
                >
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>
      </v-list>

      <div class="d-flex align-center edit-btn" @click.prevent="handleEdit">
        <v-icon class="d-none d-md-inline-flex psdarkgreen--text mr-1"
          >mdi-pencil</v-icon
        >
        <a
          href="#"
          class="d-flex align-center psdarkgreen--text font-weight-semi-bold text-capitalize text-underlined"
          :class="$vuetify.breakpoint.mdAndUp ? 'font-20' : 'font-16'"
          aria-label="Edit Property Details"
          >Edit</a
        >
      </div>
    </v-card>

    <!-- No bedrooms/bathrooms error message -->
    <v-alert
      v-if="showNumberOfRoomsErrorMessage"
      color="var(--red-100)"
      class="mt-4"
    >
      <div class="d-flex flex-no-wrap">
        <div class="mr-3">
          <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
        </div>
        <div>
          <span class="font-weight-bold">Sorry!</span> Records indicate this
          property has more than 8 {{ numberOfRoomsMessageText }} so
          unfortunately we can not provide a quote at this time. If this is
          incorrect, please click 'Edit' and enter the correct amount.
        </div>
      </div>
    </v-alert>

    <!-- Wall/Roof type other error message -->
    <v-alert
      v-if="isWallTypeOther || isRoofTypeOther"
      color="var(--red-100)"
      class="mt-4"
    >
      <div class="d-flex flex-no-wrap">
        <div class="mr-3">
          <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
        </div>
        <div>
          <span class="font-weight-bold">Sorry!</span>&nbsp;Please check the
          data returned for the wall/roof type for this property and click
          ‘Edit’ to select the correct wall/roof type from the options
          available.
        </div>
      </div>
    </v-alert>

    <!-- Check details are correct message -->
    <v-alert
      v-if="!isWallTypeOther && !isRoofTypeOther"
      color="var(--orange-100)"
      class="mt-4 mb-0 note-warning border-sm border-solid border-warning"
    >
      <div class="d-flex flex-no-wrap">
        <div class="mr-3">
          <v-icon size="45" color="var(--orange-500)">mdi-alert</v-icon>
        </div>
        <div class="primary--text">
          <span class="font-weight-bold">Warning!</span>&nbsp;It’s important
          that the property details above are correct. Click ‘Edit’ if you need
          to change any details.
        </div>
      </div>
    </v-alert>

    <v-alert
      color="var(--blue-100)"
      class="mt-4 note-warning border-sm border-solid border-info"
    >
      <div class="d-flex flex-no-wrap">
        <div class="mr-3">
          <v-icon>icon-bath</v-icon>
        </div>
        <div class="primary--text">
          <span class="font-weight-bold">Note:</span>&nbsp;Bathrooms include
          en-suites, wetrooms, toilet rooms and shower rooms, the number above
          should include these.
        </div>
      </div>
    </v-alert>
  </div>
</template>

<script>
import { wordsToNumbers } from "@/common/answers";
import { ROOF_TYPE, WALL_TYPE } from "@/constants/property-detail.constant";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import { mapActions } from "vuex";

export default {
  name: "PropertyDetailsCard",
  props: {
    propertyDetails: {
      type: Object,
      required: true,
    },
    className: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      illegalNumberOfRooms: "NinePlus",
    };
  },
  computed: {
    propertyTypeText() {
      const propertyType = {
        DetachedHouse: "Detached house",
        SemiDetachedHouse: "Semi-detached house",
        Terraced: "Terraced house",
        DormerBungalow: "Dormer bungalow",
        DetachedBungalow: "Detached bungalow",
        SemiDetachedBungalow: "Semi-detached bungalow",
        Flat: "Flat",
      }[this.propertyDetails.PropertyType];
      return propertyType;
    },
    roofTypeText() {
      function getRoofType(value) {
        return {
          Tile: "Tile",
          Slate: "Slate",
          FeltOnTimber: "Felt on Timber",
          Concrete: "Concrete",
          Metal: "Metal",
          Timber: "Timber",
          Asphalt: "Asphalt",
          Shingle: "Shingle",
          Stramit: "Stramit",
          ThatchFibre: "Thatch fibre",
          ThatchReed: "Thatch reed",
          Other: "Other",
        }[value];
      }
      const roofType = getRoofType(this.propertyDetails.RoofConstruction);
      return roofType + " roof";
    },
    wallTypeText() {
      function getWallType(value) {
        return {
          Brick: "Brick",
          Concrete: "Concrete",
          Stone: "Stone",
          Flint: "Flint",
          Timber: "Timber",
          BrickOverTimber: "Brick over Timber frame",
          Metal: "Metal",
          Other: "Other",
        }[value];
      }
      const wallType = getWallType(this.propertyDetails.WallConstruction);
      return wallType + " walls";
    },
    isWallTypeOther() {
      return (
        this.wallTypeText === WALL_TYPE.OTHER ||
        this.wallTypeText === WALL_TYPE.OTHER_WALLS
      );
    },
    isRoofTypeOther() {
      return (
        this.roofTypeText === ROOF_TYPE.OTHER ||
        this.roofTypeText === ROOF_TYPE.OTHER_ROOF
      );
    },
    numberOfBedroomsText() {
      return this.matchNumberWordToDisplayText(
        this.propertyDetails.NumberOfBedrooms,
        " bedrooms",
        " bedroom"
      );
    },
    numberOfBathroomsText() {
      return this.matchNumberWordToDisplayText(
        this.propertyDetails.NumberOfBathrooms,
        " bathrooms",
        " bathroom"
      );
    },
    numberOfRoomsMessageText() {
      const bedroomAnswer = this.propertyDetails.NumberOfBedrooms;
      const bathroomAnswer = this.propertyDetails.NumberOfBathrooms;

      if (
        bedroomAnswer === this.illegalNumberOfRooms &&
        bathroomAnswer === this.illegalNumberOfRooms
      ) {
        return "bedrooms and bathrooms";
      }
      if (bedroomAnswer === this.illegalNumberOfRooms) {
        return "bedrooms";
      }
      if (bathroomAnswer === this.illegalNumberOfRooms) {
        return "bathrooms";
      }
      return "";
    },
    showNumberOfRoomsErrorMessage() {
      return (
        this.propertyDetails.NumberOfBedrooms === this.illegalNumberOfRooms ||
        this.propertyDetails.NumberOfBathrooms === this.illegalNumberOfRooms
      );
    },
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, ["setPropertyDetailsFormVisibility"]),
    handleEdit() {
      this.$emit("emitEditButtonValue");
      this.setPropertyDetailsFormVisibility(true);
    },
    matchNumberWordToDisplayText(numberWord, pluralText, singularText) {
      const number = wordsToNumbers(numberWord);
      return typeof number === "string" || number > 1
        ? number + pluralText
        : number + singularText;
    },
  },
};
</script>

<style lang="scss" scoped>
.theme--light.v-list-item:not(.v-list-item--active):not(.v-list-item--disabled) {
  color: $black-2;
}

.margin-property-details-card {
  margin: -3px 0 13px 0;
}

.property-details {
  background-color: $gray-light-4;
  border: solid 2px $black-2;
  position: relative;

  .edit-btn {
    position: absolute;
    cursor: pointer;
  }

  .v-list {
    background-color: $gray-light-4;

    h3 {
      font-size: 20px;
    }

    .v-list-item__title {
      font-size: 18px;
      line-height: 20px;
    }
  }
}

.v-list-item--dense .v-list-item__icon,
.v-list--dense .v-list-item .v-list-item__icon {
  height: 20px !important;
  margin-top: 9px !important;
  margin-bottom: 0 !important;
}

.v-list-item--dense,
.v-list--dense .v-list-item {
  min-height: 32px !important;
}

@media screen and (min-width: 0) {
  .property-details {
    .edit-btn {
      top: 19px;
      right: 19px;
    }

    .v-list {
      h3 {
        width: 220px;
        font-size: 20px;
      }
    }
  }
}

@media screen and (min-width: ($grid-breakpoints-sm - 1)) {
  .property-details {
    .edit-btn {
      top: 22px;
      right: 36px;
    }

    .v-list {
      h3 {
        width: 100%;
      }
    }
  }
}
.layout-container {
  margin-bottom: -16px !important;
}
</style>
